import { Languages, LogOut, Moon, Search, ShieldAlert, ShieldCheck, ShieldOff, Sun } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSystemHealth } from "@/hooks/useSystemHealth";
import { useTheme } from "@/hooks/useTheme";
import { t, lang, setLang, type Lang } from "@/lib/i18n";
import type { LicenseStatusCode } from "@/lib/types";

const LICENSE_META: Record<
  LicenseStatusCode,
  { Icon: typeof ShieldCheck; tone: string; label: string }
> = {
  full: { Icon: ShieldCheck, tone: "text-muted-foreground", label: t("Lizenz aktiv") },
  grace: { Icon: ShieldAlert, tone: "text-warning", label: t("Grace-Period") },
  read_only: { Icon: ShieldOff, tone: "text-destructive", label: t("Read-Only") },
  unlicensed: { Icon: ShieldOff, tone: "text-destructive", label: t("Keine Lizenz") },
};

/**
 * Kopfzeile. Auf Mobil (< md) reduziert auf Logo · Suche · Verbindungspunkt ·
 * Lizenz-Icon — Sprache, Theme und Konto liegen dort im „Mehr"-Sheet der
 * MobileNav. `pt-safe` schiebt die Zeile unter die iOS-Statusleiste
 * (black-translucent im Standalone-Modus).
 */
export function Header({ onOpenPalette }: { onOpenPalette: () => void }) {
  const { username, logout } = useAuth();
  const { license, connection } = useSystemHealth();
  const { dark, toggle } = useTheme();

  const initials = (username ?? "?").slice(0, 2).toUpperCase();
  const live = connection === "ok";
  const licenseMeta = license ? LICENSE_META[license.status] : null;

  return (
    <header className="shrink-0 border-b border-border bg-card pt-safe">
      <div className="flex h-14 items-center gap-2 px-3 md:gap-3 md:px-4">
        {/* Mobil ersetzt das Logo den Sidebar-Kopf. */}
        <img src="/logo.png" alt="Leif" className="h-7 w-7 shrink-0 rounded-md md:hidden" />

        <button
          type="button"
          onClick={onOpenPalette}
          className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-md border border-border bg-muted/40 px-3 text-left text-sm text-muted-foreground transition-colors hover:bg-muted md:h-auto md:py-1.5"
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className="truncate md:hidden">{t("Suche…")}</span>
          <span className="hidden truncate md:inline">{t("Suche oder Befehl…")}</span>
          <kbd className="ml-auto hidden rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] md:inline">
            ⌘K
          </kbd>
        </button>

        <div
          className={
            "flex shrink-0 items-center gap-1.5 text-xs " + (live ? "text-success" : "text-destructive")
          }
          title={live ? t("Verbindung zum Server steht") : t("Verbindung verloren")}
        >
          <span
            className={
              "inline-block h-2 w-2 rounded-full " +
              (live ? "bg-success" : "bg-destructive animate-pulse")
            }
          />
          <span className="hidden sm:inline">{live ? t("Live") : t("Offline")}</span>
        </div>

        {licenseMeta ? (
          <div
            className={`flex shrink-0 items-center gap-1 text-xs ${licenseMeta.tone}`}
            title={licenseMeta.label}
          >
            <licenseMeta.Icon className="h-3.5 w-3.5" />
            <span className="hidden md:inline">{licenseMeta.label}</span>
          </div>
        ) : null}

        {/* Ab hier Desktop-only — mobil im „Mehr"-Sheet (MobileNav). */}
        <label
          className="hidden items-center gap-1 text-muted-foreground transition-colors hover:text-foreground md:flex"
          title={t("Sprache")}
        >
          <Languages className="h-4 w-4" />
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value as Lang)}
            aria-label={t("Sprache")}
            className="cursor-pointer rounded-md border-none bg-transparent py-1 pr-1 text-xs uppercase text-muted-foreground outline-none hover:text-foreground"
          >
            <option value="de">DE</option>
            <option value="en">EN</option>
            <option value="fr">FR</option>
          </select>
        </label>

        <button
          type="button"
          onClick={toggle}
          className="hidden rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:inline-flex"
          aria-label={t("Theme umschalten")}
        >
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        <div className="hidden items-center gap-2 md:flex">
          <div
            className="flex h-7 w-7 items-center justify-center rounded-full bg-accent text-xs font-medium text-accent-foreground"
            title={username ?? undefined}
          >
            {initials}
          </div>
          <button
            type="button"
            onClick={logout}
            aria-label={t("Abmelden")}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title={t("Abmelden")}
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
